import { useEffect, useState } from "react";
import axios from "axios";
import {
  Bot, BookOpen, Cpu, Eye, Boxes, Orbit, Wifi, Glasses, MonitorCog, Workflow,
  ToggleLeft, ToggleRight, Plus, Pencil, Trash2,
} from "lucide-react";
import { authHeader } from "@/hooks/useAdminAuth";
import API from "@/lib/api";
import AdminFormModal, { Field, TextInput, NumberInput, TextArea, Select } from "@/components/admin/AdminFormModal";
import ImageUpload from "@/components/admin/ImageUpload";

const ICONS = { Bot, BookOpen, Cpu, Eye, Boxes, Orbit, Wifi, Glasses, MonitorCog, Workflow };
const ICON_KEYS = Object.keys(ICONS);

const EMPTY = {
  title: "",
  short_desc: "",
  build_items: "",
  value: "",
  icon: "Cpu",
  image_url: "",
  sort_order: 0,
  is_active: true,
};

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/services?admin=true`)
      .then(({ data }) => setServices(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY, sort_order: services.length });
    setModalOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      title: s.title || "",
      short_desc: s.short_desc || "",
      build_items: Array.isArray(s.build_items) ? s.build_items.join("\n") : "",
      value: s.value || "",
      icon: s.icon || "Cpu",
      image_url: s.image_url || "",
      sort_order: s.sort_order ?? 0,
      is_active: s.is_active ?? true,
    });
    setModalOpen(true);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        sort_order: Number(form.sort_order) || 0,
        build_items: form.build_items
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      if (editing) {
        await axios.put(`${API}/services/${editing.id}`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API}/services`, payload, { headers: authHeader() });
      }
      setModalOpen(false);
      load();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, current) => {
    try {
      await axios.put(`${API}/services/${id}`, { is_active: !current }, { headers: authHeader() });
      setServices((ss) => ss.map((s) => (s.id === id ? { ...s, is_active: !current } : s)));
    } catch (err) {
      console.error(err);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete service "${s.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/services/${s.id}`, { headers: authHeader() });
      setServices((ss) => ss.filter((x) => x.id !== s.id));
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7B2CBF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white font-display">Services</h1>
          <p className="text-gray-500 mt-1 text-sm">{services.length} capabilities</p>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-[#00A8E8] to-[#7B2CBF] text-white hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" /> New Service
        </button>
      </div>

      <div className="space-y-3">
        {services.map((s) => {
          const Icon = ICONS[s.icon] || Cpu;
          return (
            <div
              key={s.id}
              className={`bg-[#0D0E13] border border-white/5 rounded-xl px-5 py-4 flex items-center gap-4 transition-opacity ${
                s.is_active ? "" : "opacity-50"
              }`}
            >
              <div className="w-10 h-10 rounded-xl bg-[#7B2CBF]/15 border border-[#7B2CBF]/25 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#7B2CBF]" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="text-white font-medium text-sm">{s.title}</div>
                <div className="text-gray-500 text-xs mt-0.5 line-clamp-1">{s.short_desc}</div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                    s.is_active
                      ? "bg-green-400/15 text-green-400 border-green-400/25"
                      : "bg-gray-500/15 text-gray-400 border-gray-500/25"
                  }`}
                >
                  {s.is_active ? "Active" : "Hidden"}
                </span>
                <button
                  onClick={() => toggleActive(s.id, s.is_active)}
                  className="text-gray-500 hover:text-white transition-colors p-1"
                  title={s.is_active ? "Hide" : "Show"}
                >
                  {s.is_active ? (
                    <ToggleRight className="w-6 h-6 text-green-400" />
                  ) : (
                    <ToggleLeft className="w-6 h-6" />
                  )}
                </button>
                <button
                  onClick={() => openEdit(s)}
                  className="text-gray-500 hover:text-[#00A8E8] transition-colors p-1"
                  title="Edit"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => remove(s)}
                  className="text-gray-500 hover:text-red-400 transition-colors p-1"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <AdminFormModal
        open={modalOpen}
        title={editing ? "Edit Service" : "New Service"}
        onClose={() => setModalOpen(false)}
        onSubmit={submit}
        submitting={saving}
        submitLabel={editing ? "Update Service" : "Create Service"}
      >
        <Field label="Title" required>
          <TextInput value={form.title} onChange={(e) => update("title", e.target.value)} required />
        </Field>
        <Field label="Short Description" required>
          <TextArea value={form.short_desc} onChange={(e) => update("short_desc", e.target.value)} required />
        </Field>
        <Field label="What We Can Build" hint="One item per line">
          <TextArea
            rows={5}
            value={form.build_items}
            onChange={(e) => update("build_items", e.target.value)}
            placeholder={"AI assistants for operations\nMulti-agent task orchestration"}
          />
        </Field>
        <Field label="Business Value">
          <TextArea value={form.value} onChange={(e) => update("value", e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Icon">
            <Select value={form.icon} onChange={(e) => update("icon", e.target.value)}>
              {ICON_KEYS.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Image" hint="Upload a file or paste a URL">
          <ImageUpload value={form.image_url} onChange={(v) => update("image_url", v)} />
        </Field>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => update("is_active", e.target.checked)}
            className="w-4 h-4 accent-[#7B2CBF]"
          />
          <span className="text-sm text-white">Active (visible on site)</span>
        </label>
      </AdminFormModal>
    </div>
  );
}
